import { useState } from 'react';
import { Box, Card, Typography, Chip, Button } from '@mui/material';
import LocationOn from '@mui/icons-material/LocationOn';
import BotSelection from '../ChatMessenger/bot-profile-chat/BotSelection';
import ChatInitiation from '../ChatMessenger/bot-profile-chat/ChatInitiation';
import { useEffect } from 'react';
import { getBotUsers } from '../../api/user';
import useSocket from '../../hooks/useSocket';
import { toast } from 'react-toastify';
import { getAvatarByGender } from '../../utils/CommonUtils';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export default function BotOnlyModal({
  onClose,
  step,
  setStep,
  onBotViewButtonClick,
  onRealUserClick,
  selectedUser,
}) {
  // const [step, setLocalStep] = useState(2); // Start from Step 2 (No Step 1)
  const [selectedBot, setSelectedBot] = useState(null);
  const [botDistance, setBotDistance] = useState([40]);
  const [messageTemplate, setMessageTemplate] = useState('');
  const [bots, setBots] = useState([]);
  const [loadingBots,setLoadingBots] = useState(false)
  const [totalCount, setTotalCount] = useState(0);
  const { socket, sendMessage, } = useSocket();

  // const bots = [
  //   { id: '1', username: '@Alexander001', distance: '5 km away', images: ["/assets/images/user-profile/fancy-images/img1.jpg"], hasStatus: true },
  //   { id: '2', username: '@SarahB21', distance: '8 km away', images: ["/assets/images/user-profile/fancy-images/img2.jpg"] },
  //   { id: '3', username: '@JamesW9n', distance: '12 km away', images: ["/assets/images/user-profile/fancy-images/img3.jpg"] },
  //   { id: '4', username: '@MichaelT12', distance: '15 km away', images: ["/assets/images/user-profile/fancy-images/img4.jpg"] },
  // ];

  // User data from your array
  const users = [
    {
      id: 1,
      name: (selectedUser?.firstName ?? selectedUser?.userName) + ', ' + selectedUser?.age,
      location: selectedUser?.city,
      status: selectedUser?.isOnLine ? "Online" : "Offline",
      active: selectedUser?.lastActiveTime,
      plan: "gold",
      avatar: selectedUser?.profilePicture,
      isOnline: selectedUser?.isOnLine
    },
  ];

  // Use selectedUser prop if available, otherwise use the first user from users array
  const displayUser = selectedUser || users[0];
  const handleBotSelect = (bot) => {
    setSelectedBot(bot);
    // setLocalStep(3);
    setStep(3);
  };

  const handleCancel = () => {
    // setLocalStep(2);
    setSelectedBot(null);
    setMessageTemplate('');
    setStep(2);
  };

  const handleInitiateChat = (scheduledDateTime = null) => {
    if (messageTemplate.trim() === '') return;

    const messageData = {
      senderId: selectedBot.userId,
      receiverId: selectedUser._id,
      roomId: null,
      message: messageTemplate,
      messageType: 'text',
      files: [],
      userAgentSent: null,
      scheduledDateTime: scheduledDateTime,
      schedule: scheduledDateTime ? true : false
    };
    sendMessage(messageData);
    toast.success('Message sent successfully');
    onClose();
  };

  useEffect(() => {
    const fetchBots = async () => {
      try {
        setLoadingBots(true)
        const response = await getBotUsers(displayUser._id, botDistance,selectedUser?.preferences?.gender?.[0]);
        setBots(response.data);
        setTotalCount(response.count);
      } catch (error) {
        console.error('Error fetching bots:', error);
      }
        setLoadingBots(false)
    };
    if (displayUser._id) fetchBots();
  }, [displayUser._id, botDistance]);

  // Defensive: every step wrapper below is gated on selectedUser/selectedBot and
  // has no else, so without a user this component renders an empty box — that
  // was the blank "Initiate New Chat" dialog. The parent gates on a loaded user
  // too; this keeps the failure visible rather than silent if that ever changes.
  if (!selectedUser) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No member selected.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      {/* -------- Step 2 (User Selected Card + Bot Selection) -------- */}
      {step === 2 && selectedUser && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Selected User Card - Using displayUser */}
          <Card sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: '#916008',
            bgcolor: 'rgba(25, 118, 210, 0.04)'
          }}>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>

                {/* Left Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1 }}>

                  {/* Avatar */}
                  <Box sx={{ position: 'relative' }}>
                    <img
                      src={displayUser.profilePicture || getAvatarByGender(displayUser.gender)}
                      alt={(displayUser.firstName ?? displayUser.userName)}
                      style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover' }}
                    />

                    {/* Online Indicator */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 2,
                        right: 2,
                        width: 16,
                        height: 16,
                        bgcolor: displayUser.isOnLine ? 'success.main' : 'grey.500',
                        borderRadius: '50%',
                        border: '2px solid white'
                      }}
                    />
                  </Box>

                  {/* User Info */}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                     
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyItems: 'center', gap: 2, mb: 1 }}>
                        <Typography sx={{ color: '#3C4043', fontSize: '18px', fontWeight: '600', mb: 0 }}>
                          {`${(displayUser?.firstName ?? displayUser?.userName) || ""}${displayUser?.age ? `, ${displayUser?.age}` : ""}`}
                        </Typography>
                        <Box sx={{ color: displayUser?.appInstalled ? '#108523' : '#fc2323', display: 'flex', alignItems: 'center', gap: 2 }}>{displayUser?.appInstalled ? <Box><CheckCircleIcon sx={{ color: '#09a323' }} /> App Installed</Box> : <Box><CancelIcon />App Not Installed</Box>}</Box>
                      </Box>
                      {/* Plan Badge */}
                      {displayUser?.plan && (
                        <Chip
                          size="small"
                          label={displayUser?.plan?.toUpperCase()}
                          sx={{
                            mt: 0,
                            background:
                              displayUser?.plan === "gold" ? "linear-gradient(45deg, #916008, #cc9933)" :
                                "linear-gradient(90deg, rgb(176, 37, 33) 0%, rgb(234, 67, 53) 100%)",
                            color: "#fff",
                            fontSize: '12px',
                            height: '25px'
                          }}
                        />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {`${displayUser?.city || ""}${displayUser?.country ? `, ${displayUser?.country}` : ""}`}
                      </Typography>
                    </Box>

                    {/* <Typography variant="body2" color="text.secondary">
                      {displayUser.status}
                    </Typography> */}
                  </Box>
                </Box>


                {/* Right Buttons */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  {/* Active Time */}
                  {/* <Typography variant="caption" sx={{ color: '#555' }}>
                    {displayUser.active}
                  </Typography> */}
                  {/* <Chip label="Selected" size="small" sx={{ background: '#916008', color: '#fff' }} /> */}
                  <Button
                    onClick={onRealUserClick}
                    sx={{
                      mt: 1,
                      textTransform: 'capitalize',
                      fontSize: '12px',
                      background: '#BE1F1F',
                      color: '#fff',
                      borderRadius: '20px',
                      px: 2,
                      '&:hover': {
                        background: '#ff4A4A'
                      }
                    }}
                  >
                    View Profile
                  </Button>
                </Box>

              </Box>
            </Box>
          </Card>

          {/* Bot Selection Component */}
          <BotSelection
            bots={bots}
            loadingBots={loadingBots}
            selectedBot={selectedBot}
            onBotSelect={handleBotSelect}
            botDistance={botDistance}
            setBotDistance={setBotDistance}
            selectedUserName={(displayUser.firstName ?? displayUser.userName)}
            onBotViewButtonClick={onBotViewButtonClick}
            selectedUserId={displayUser._id}
          />
        </Box>
      )}

      {/* -------- Step 3 (Chat Initiation) -------- */}
      {step === 3 && selectedBot && (
        <ChatInitiation
          selectedBot={selectedBot}
          selectedUser={displayUser}           
          messageTemplate={messageTemplate}
          setMessageTemplate={setMessageTemplate}
          onCancel={handleCancel}
          onInitiateChat={handleInitiateChat}
          onBotSelect={handleBotSelect}
          onBotViewButtonClick={onBotViewButtonClick}
          onRealUserClick={onRealUserClick}
        />
      )}
    </Box>
  );
}